import {
  ChangeEvent,
  ChangeEventHandler,
  KeyboardEvent,
  MouseEventHandler,
  KeyboardEventHandler,
  MouseEvent,
} from 'react'
import { useLanguageContext } from '../../contexts/LanguageContext'
import { useTheme } from '../../hooks/useTheme'
import styles from './buttonToggle.module.css'

interface Props {
  id: string | number
  on: string
  off: string
  name: string
  className?: string
  wrapperClass?: string
  onChange:
    | ChangeEventHandler<HTMLInputElement>
    | MouseEventHandler<HTMLInputElement>
    | MouseEventHandler<HTMLButtonElement>
    | KeyboardEventHandler<HTMLButtonElement>
  isChecked: boolean
  equal?: boolean
  label: string
  hideLabel?: boolean
  hideOnOff?: boolean
  unavailable?: boolean
  unavailableReason?: string
}
const ButtonToggle = ({
  id,
  on,
  off,
  name,
  className,
  wrapperClass,
  isChecked,
  onChange,
  equal,
  label,
  hideLabel,
  hideOnOff,
  unavailable,
  unavailableReason,
}: Props) => {
  const { t } = useLanguageContext()
  const lightTheme = useTheme()
  const labelId = `toggle-label-${id}`
  const toggleId = `toggle-${id}`
  const stateId = `toggle-state-${id}`
  const unavailableId = `toggle-unavailable-${id}`
  const describedBy = [
    stateId,
    unavailable && unavailableReason ? unavailableId : undefined,
  ]
    .filter(Boolean)
    .join(' ')

  const preventUnavailableAction = (
    event:
      | ChangeEvent<HTMLInputElement>
      | KeyboardEvent<HTMLInputElement>
      | MouseEvent<HTMLInputElement>
      | MouseEvent<HTMLLabelElement>
  ) => {
    if (!unavailable) return false

    event.preventDefault()
    event.stopPropagation()
    return true
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (preventUnavailableAction(event)) return
    ;(onChange as ChangeEventHandler<HTMLInputElement>)(event)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      unavailable &&
      (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar')
    ) {
      preventUnavailableAction(event)
    }
  }

  const handleClick = (event: MouseEvent<HTMLLabelElement>) => {
    preventUnavailableAction(event)
  }

  return (
    <div
      className={`${styles['toggle-container']} toggle-container ${className}-container ${wrapperClass} ${hideOnOff ? styles.small : ''} ${unavailable ? styles.disabled : ''}`}
      aria-labelledby={labelId}
      role="group"
      aria-disabled={unavailable}
    >
      <span
        id={labelId}
        className={`label ${hideLabel ? 'scr' : ''}`}
        aria-live="polite"
      >
        {label}
      </span>
      <label
        htmlFor={toggleId}
        className={`${styles.toggle} toggle tooltip-wrap ${className} focus-within ${
          lightTheme ? styles.light : ''
        } ${equal ? styles.equal : ''}`}
        onClick={handleClick}
      >
        <span id={stateId} className="scr">
          {isChecked ? on : off}
        </span>
        <input
          id={toggleId}
          type="checkbox"
          name={name}
          checked={isChecked}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          className="scr"
          role="switch"
          aria-disabled={unavailable}
          aria-labelledby={labelId}
          aria-describedby={describedBy || undefined}
        />
        <span className={`${styles.slider} slider`} aria-hidden="true"></span>
        <span
          className={`${styles.labels} labels ${hideOnOff ? styles['hide-on-off'] : ''}`}
          data-on={on}
          data-off={off}
          aria-hidden="true"
        ></span>
        {unavailable && unavailableReason ? (
          <>
            <span className="tooltip below narrow2" role="tooltip">
              {`${t('Unavailable')}: ${unavailableReason}`}
            </span>
            <span id={unavailableId} className="scr">
              {`${t('Unavailable')}: ${unavailableReason}`}
            </span>
          </>
        ) : null}
      </label>
    </div>
  )
}

export default ButtonToggle
